import { z } from 'zod';
/**
 * F-006 — Archivio Permanente & Download Ricevuta
 *
 * Schemi Zod condivisi tra API (validation) e frontend (forms / typed fetch)
 * per la feature di archivio pratiche con ricerca full-text italiana, filtri,
 * paginazione cursor-based e download sicuro via presigned URL S3 IONOS.
 *
 * Pattern: ogni schema esporta sia lo schema Zod sia il tipo TypeScript inferito
 * (`z.infer<typeof schema>`). Per gli input controllati dal client si usa
 * `.strict()` come regression guard anti mass-assignment / path injection.
 */
/* -------------------------------------------------------------------------- */
/* FileTypeEnum + DownloadRequestSchema                                        */
/* -------------------------------------------------------------------------- */
/**
 * Tipi documento scaricabili dall'archivio.
 *
 * - CONTRATTO_PDF: contratto di locazione PDF generato a inizio pratica.
 * - RICEVUTA_PDF:  ricevuta di registrazione AdE in formato PDF.
 * - RICEVUTA_XML:  XML telematico Entratel della ricevuta AdE.
 * - FATTURA_SDI:   fattura elettronica SDI emessa al cliente.
 *
 * L'enum è la fonte di verità per il mapping server-side fileType → S3 key.
 * Il client NON deve mai inviare un path/key esplicito.
 */
export const FileTypeEnum = z.enum([
    'CONTRATTO_PDF',
    'RICEVUTA_PDF',
    'RICEVUTA_XML',
    'FATTURA_SDI',
]);
/**
 * Body schema per `POST /api/v1/archivio/:id/download`.
 *
 * SICUREZZA: `.strict()` rifiuta qualsiasi campo extra oltre a `fileType`.
 * Regression guard contro tentativi di inviare un path S3 lato client che
 * permetterebbe l'enumeration di oggetti di altri tenant. La mappa
 * fileType → S3 key è esclusivamente server-side.
 */
export const DownloadRequestSchema = z
    .object({
    fileType: FileTypeEnum,
})
    .strict();
/* -------------------------------------------------------------------------- */
/* ArchivioCursor + encode/decode helpers                                      */
/* -------------------------------------------------------------------------- */
/**
 * Cursor di paginazione cursor-based su `(createdAt DESC, id DESC)`.
 *
 * Più stabile dell'offset sotto insert concorrenti e compatibile con scroll
 * infinito virtualizzato. Il cursor viene serializzato in base64url nel
 * query string per essere opaco al consumer (`opaque token`).
 */
export const ArchivioCursorSchema = z.object({
    createdAt: z.string().datetime(),
    id: z.string().cuid(),
});
/**
 * Codifica/decodifica base64url isomorfica (Node + browser).
 *
 * `packages/shared` non include i tipi Node (`@types/node`) per restare
 * runtime-agnostic — quindi `Buffer` non è tipato. Usiamo l'API standard
 * `btoa`/`atob` con sostituzione dei caratteri url-safe `+/=` → `-_` (e
 * inverso in decode). Implementazione semantica identica a
 * `Buffer.from(s).toString('base64url')`.
 */
function utf8ToBase64Url(input) {
    // Encode UTF-8 string → bytes → base64 → base64url (no padding).
    const bytes = new TextEncoder().encode(input);
    let binary = '';
    for (const b of bytes)
        binary += String.fromCharCode(b);
    const b64 = btoa(binary);
    return b64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/g, '');
}
function base64UrlToUtf8(input) {
    // Re-pad base64url → base64 → bytes → UTF-8 string.
    const b64 = input.replace(/-/g, '+').replace(/_/g, '/');
    const padded = b64 + '='.repeat((4 - (b64.length % 4)) % 4);
    const binary = atob(padded);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++)
        bytes[i] = binary.charCodeAt(i);
    return new TextDecoder('utf-8', { fatal: true }).decode(bytes);
}
/**
 * Codifica un cursor in stringa base64url per il query string.
 * Funzione pura — nessun side effect, nessun IO.
 */
export function encodeCursor(cursor) {
    return utf8ToBase64Url(JSON.stringify(cursor));
}
/**
 * Decodifica e valida un cursor proveniente dal client.
 * Lancia `ZodError` (schema invalido) o `Error` (base64/JSON malformato):
 * questo è il comportamento desiderato — il chiamante (route handler) tradurrà
 * l'errore in `400 INVALID_CURSOR`.
 */
export function decodeCursor(encoded) {
    const json = base64UrlToUtf8(encoded);
    return ArchivioCursorSchema.parse(JSON.parse(json));
}
/* -------------------------------------------------------------------------- */
/* ArchivioListQuerySchema                                                     */
/* -------------------------------------------------------------------------- */
/**
 * Query parameters di `GET /api/v1/archivio`.
 *
 * - `q`         testo libero per FTS italiana (`plainto_tsquery('italian', ...)`).
 * - `dateFrom`  inclusivo (ISO 8601, applicato su `createdAt`).
 * - `dateTo`    inclusivo (ISO 8601, applicato su `createdAt`).
 * - `tipologia` filtro esatto su tipologia contratto.
 * - `stato`     filtro esatto su stato pratica.
 * - `parte`     filtro per ruolo: 'proprietario' | 'inquilino' (target del match `q`).
 * - `cursor`    base64url di `ArchivioCursor` (opaque token).
 * - `take`      page size; default 50, max 100 (coerced da stringa querystring).
 */
export const ArchivioListQuerySchema = z.object({
    q: z.string().trim().max(200).optional(),
    dateFrom: z.string().datetime().optional(),
    dateTo: z.string().datetime().optional(),
    tipologia: z.string().optional(),
    stato: z.string().optional(),
    parte: z.enum(['proprietario', 'inquilino']).optional(),
    cursor: z.string().optional(),
    take: z.coerce.number().int().min(1).max(100).default(50),
});
/* -------------------------------------------------------------------------- */
/* PraticaListItem + ArchivioListResponse                                      */
/* -------------------------------------------------------------------------- */
/**
 * Riga della tabella archivio. Subset dei campi Pratica sufficiente per la
 * lista (6 colonne visibili + id/createdAt usati per cursor e routing).
 */
export const PraticaListItemSchema = z.object({
    id: z.string().cuid(),
    createdAt: z.string().datetime(),
    tipologia: z.string(),
    proprietarioNome: z.string(),
    proprietarioCF: z.string().optional(),
    inquilinoNome: z.string(),
    inquilinoCF: z.string().optional(),
    indirizzoImmobile: z.string(),
    stato: z.string(),
});
/**
 * Risposta paginata di `GET /api/v1/archivio`.
 *
 * NOTA: NO campo `total` — la paginazione cursor-based non conta le righe.
 *
 * `hasAnyPratiche` è incluso (e ha senso) solo quando `items.length === 0`,
 * per distinguere lato UI tra `EmptyArchivio` (nessuna pratica in assoluto)
 * e `NoResultsState` (ci sono pratiche ma i filtri non matchano).
 */
export const ArchivioListResponseSchema = z.object({
    items: z.array(PraticaListItemSchema),
    nextCursor: z.string().nullable(),
    hasMore: z.boolean(),
    hasAnyPratiche: z.boolean().optional(),
});
/* -------------------------------------------------------------------------- */
/* CronologiaEntry + DocumentoInfo + PraticaDetailView                         */
/* -------------------------------------------------------------------------- */
/**
 * Voce della cronologia stati pratica (timeline milestone).
 * Le 4 milestone canoniche del workflow AdE.
 */
export const CronologiaEntrySchema = z.object({
    milestone: z.enum(['INVIATA', 'PRESA_IN_CARICO', 'IN_ELABORAZIONE', 'COMPLETATA']),
    at: z.string().datetime(),
});
/**
 * Metadata di un documento associato a una pratica.
 * Quando `available === false`, gli altri campi sono assenti.
 */
export const DocumentoInfoSchema = z.object({
    available: z.boolean(),
    lastModifiedAt: z.string().datetime().optional(),
    sizeBytes: z.number().int().positive().optional(),
});
/**
 * Vista dettaglio di una singola pratica (`GET /api/v1/archivio/:id`).
 * Include dati anagrafici, riferimenti documenti e cronologia stati.
 */
export const PraticaDetailViewSchema = z.object({
    id: z.string().cuid(),
    createdAt: z.string().datetime(),
    tipologia: z.string(),
    stato: z.string(),
    proprietarioNome: z.string(),
    proprietarioCF: z.string().optional(),
    inquilinoNome: z.string(),
    inquilinoCF: z.string().optional(),
    indirizzoImmobile: z.string(),
    cittaImmobile: z.string().optional(),
    canoneMensile: z.number().optional(),
    dataStipula: z.string().datetime().optional(),
    protocolloAde: z.string().optional(),
    documenti: z.object({
        contrattoPdf: DocumentoInfoSchema,
        ricevutaPdf: DocumentoInfoSchema,
        ricevutaXml: DocumentoInfoSchema,
        fatturaSdi: DocumentoInfoSchema,
    }),
    cronologia: z.array(CronologiaEntrySchema),
});
/* -------------------------------------------------------------------------- */
/* PresignedUrlResponse                                                        */
/* -------------------------------------------------------------------------- */
/**
 * Risposta di `POST /api/v1/archivio/:id/download`.
 *
 * `url` è un presigned URL S3 IONOS con TTL 10 minuti (600s).
 * `expiresAt` è l'istante di scadenza in ISO 8601 (uso UI countdown).
 * `filename` è il nome suggerito per il download (Content-Disposition).
 */
export const PresignedUrlResponseSchema = z.object({
    url: z.string().url(),
    expiresAt: z.string().datetime(),
    filename: z.string(),
});
//# sourceMappingURL=archivio.js.map